import React, { MouseEventHandler, useMemo } from "react";
import { TournamentStatus } from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import "./LiveMultiplayerTournamentsSection.scss";
import { ROUTES } from "../../../../utils/constants";
import DownloadAppPrompt, {
  PROMPT_DOWNLOAD_SCENARIOS,
} from "../../../DownloadAppPrompt/DownloadAppPrompt";
import useDisclosure from "../../../../hooks/useDisclosure";
import Skeleton from "../../../Skeleton/Skeleton";
import TournamentCard from "../../../tournament/TournamentCard/TournamentCard";
import TournamentCardSkeleton from "../../../tournament/TournamentCard/TournamentCardSkeleton";
import useInfiniteMultiplayerTournamentsQuery from "../../../../react-query/queries/use-infinite-multiplayer-tournaments";

function LoadingSkeleton() {
  return (
    <section className="live-multiplayer-section" aria-hidden="true">
      <div className="heading-container px-3 d-flex justify-content-between align-items-center">
        <Skeleton height={20} width={186} />
      </div>
      <div className="slider scrollbar-hidden px-3 d-flex overflow-auto">
        {Array.from({ length: 2 }).map((_, index) => (
          <TournamentCardSkeleton key={index} />
        ))}
      </div>
    </section>
  );
}

function LiveMultiplayerTournamentsSection() {
  const { t } = useTranslation("dashboard");
  const {
    tournaments,
    query: { isLoading },
  } = useInfiniteMultiplayerTournamentsQuery();
  const {
    isOpen: isDownloadAppPromptOpen,
    onOpen: openDownloadAppPrompt,
    onClose: closeDownloadAppPrompt,
  } = useDisclosure();

  const handleViewTournament: MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    openDownloadAppPrompt();
  };

  const orderedTournaments = useMemo(() => {
    const statusPriority = (status?: TournamentStatus) => {
      if (status === TournamentStatus.ONGOING) return 0;
      if (
        status === TournamentStatus.OPEN ||
        status === TournamentStatus.SCHEDULED
      ) {
        return 1;
      }
      if (status === TournamentStatus.COMPLETED) return 2;
      return 3;
    };

    return [...tournaments].sort(
      (first, second) =>
        statusPriority(first.status) - statusPriority(second.status)
    );
  }, [tournaments]);

  if (isLoading) return <LoadingSkeleton />;

  if (!tournaments || tournaments.length === 0) return null;

  return (
    <>
      <section className="live-multiplayer-section">
        <div className="heading-container px-3 d-flex justify-content-between align-items-center">
          <h2 className="heading">{t("live_multiplayer_tournaments")}</h2>
          <button
            className="see-all-btn"
            onClick={() => openDownloadAppPrompt()}
          >
            {t("see_all")}
          </button>
        </div>

        <div className="slider scrollbar-hidden px-3 d-flex overflow-auto">
          {orderedTournaments.map((tournament) => (
            <Link
              key={tournament.id}
              to={ROUTES.GAMES.ROOT}
              onClick={handleViewTournament}
              className="text-decoration-none tournament-container"
            >
              <TournamentCard tournament={tournament} />
            </Link>
          ))}
        </div>
      </section>
      <DownloadAppPrompt
        isOpen={isDownloadAppPromptOpen}
        onOpen={openDownloadAppPrompt}
        onClose={closeDownloadAppPrompt}
        promptDownloadScenario={
          PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.PLAY_TOURNAMENT
        }
      />
    </>
  );
}

export default LiveMultiplayerTournamentsSection;
